import { ArrowLeftIcon, ShareIcon, StopIcon } from "@heroicons/react/24/outline";
import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import type { Audit } from "@/types";
import { auditTimeLabel } from "@/utils/auditTimeLabel";
import { hostFromUrl } from "@/utils/formatters";
import ShareAuditDialog from "./ShareAuditDialog";
import StatusBadge from "./StatusBadge";

export default function AuditHeader({ audit }: { audit: Audit }) {
  const [sharingOpen, setSharingOpen] = useState(false);
  const sharingVisible = audit.status === "completed";

  function stopAudit() {
    if (
      window.confirm("Are you sure you want to stop this audit? This will stop all running or pending interactions.")
    ) {
      router.post(`/audits/${audit.id}/stop`);
    }
  }

  return (
    <>
      <section className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-start">
        <div>
          <h2 className="flex items-center text-2xl font-semibold text-gray-900">
            <Link href="/audits" className="text-primary" aria-label="Back to audits">
              <ArrowLeftIcon className="mr-1 h-5 w-5" />
            </Link>
            <span className="ml-2">{audit.scenario.objective || "Audit"}</span>
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <StatusBadge status={audit.status} />
            <p className="text-sm text-gray-500">{auditTimeLabel(audit)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {sharingVisible && (
            <div className="flex items-center gap-2">
              {audit.public && <span className="text-xs text-gray-500">Public</span>}
              <button type="button" className="btn btn-sm" onClick={() => setSharingOpen(true)}>
                <ShareIcon className="mr-1 h-4 w-4" />
                {audit.public ? "Manage Sharing" : "Share audit"}
              </button>
            </div>
          )}
          {audit.cancellable && (
            <button type="button" className="btn" onClick={stopAudit}>
              <span className="mr-2 flex h-5 w-5 items-center justify-center rounded-full border border-gray-400">
                <StopIcon className="h-3 w-3 text-gray-500" />
              </span>
              <span className="font-medium">Stop audit</span>
            </button>
          )}
        </div>
      </section>
      <ShareAuditDialog audit={audit} open={sharingOpen} onClose={() => setSharingOpen(false)} />

      <section className="card mb-6">
        <div className="card-body">
          <div className="grid gap-6 md:grid-cols-4">
            <div>
              <h3 className="mb-1 text-sm font-medium text-gray-500">Website</h3>
              {audit.scenario.start_url ? (
                <a
                  href={audit.scenario.start_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-gray hover:text-primary"
                >
                  {hostFromUrl(audit.scenario.start_url)}
                </a>
              ) : (
                <p className="font-medium text-gray-600">Not specified</p>
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-gray-200 pt-4">
            <h3 className="mb-2 text-sm font-medium text-gray-500">Instructions given to the agent</h3>
            <p className="text-gray-600">{audit.scenario.objective || "No instructions were provided."}</p>
          </div>
        </div>
      </section>
    </>
  );
}
